import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { useGetAllBranches, useUpdateManyBranches } from '@/hooks/branch.hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { BranchTableView } from '../components/BranchTableView';
import { TBranchSchema } from '@/schemas/branch/branch.schema';
import { BranchTableHeaders } from '../components/BranchTableHeaders';
import { BranchTableData } from '../components/BranchTableData';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { useGetGroupById } from '@/hooks/group.hooks';
import { useGetCompanyById } from '@/hooks/company.hooks';
import Loader from '@/components/Loading/Loader';
import { useUserRole } from '@/hooks/auth.hooks';
import { WarningMessage } from '@/components/Message/WarningMessage';

export const BranchListPage = () => {
  const { t } = useTranslation('branch');
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const navigate = useNavigate();
  const { groupId, companyId } = useParams();
  const [totalActive, setTotalActive] = useState<number>(0);

  const displayHeadingButtons = isAdmin || isGroupManager || isCompanyManager;

  const { data: group, isLoading: isLoadingGroup } = useGetGroupById(groupId);
  const { data: company, isLoading: isLoadingCompany } =
    useGetCompanyById(companyId);

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TBranchSchema>({
    t,
    useUpdateData: useUpdateManyBranches,
  });
  /**
   * === END OF Deactivate Data section ===
   */

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  return (
    <>
      <PageHeader
        backToLink={groupId ? '/groups' : companyId ? '/companies' : undefined}
        title={t('common:branches')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading
            text={
              group ? (
                <>
                  {t('common:branchesLinkedToTheGroup')}{' '}
                  <span className="font-medium">{group?.data?.name}</span>
                </>
              ) : company ? (
                <>
                  {t('common:branchesLinkedToTheCompany')}{' '}
                  <span className="font-medium">{company?.data?.name}</span>
                </>
              ) : (
                t('common:detailsRelatedToCurrentSection')
              )
            }
          />
        }
        actionListElement={
          <>
            {displayHeadingButtons && (
              <>
                <HeadingButton
                  text={t('common:importAList')}
                  leftIcon={
                    <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                  }
                  onClick={() => navigate('#')}
                />
                <HeadingButton
                  text={t('addBranch')}
                  leftIcon={
                    <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
                  }
                  onClick={() => navigate('/branch/create')}
                />
              </>
            )}
          </>
        }
      />
      <div>
        <BranchTableView<TBranchSchema>
          t={t}
          useGetAllData={useGetAllBranches}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={BranchTableHeaders}
          tableData={BranchTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage message={t('common:unableToDeactivateLinkedItem')} />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans i18nKey="common:confirmDeactivateItems" />
          ) : (
            t('common:confirmDeactivateItem')
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
      {(isLoadingGroup || isLoadingCompany) && <Loader />}
    </>
  );
};
